import type { SimpleTableColumn } from "../../../../components/common/simple-table/simple-table";
import type { AdminLoyaltyTransactionDto } from "../../../../api";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";

function isNearExpiry(expiresAt: string): boolean {
  const diff = new Date(expiresAt).getTime() - Date.now();
  return diff < 30 * 24 * 60 * 60 * 1000;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function buildAdminLoyaltyTableColumns(
  onView: (tx: AdminLoyaltyTransactionDto) => void,
): SimpleTableColumn<AdminLoyaltyTransactionDto>[] {
  return [
    {
      key: "partnerName",
      label: "Partner Name",
      headerClassName: "[flex:3] min-w-0",
      className: "[flex:3] min-w-0",
      render: (t) => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <Text variant="bodySmBold" tone="dark" className="truncate">
            {t.partnerName ?? "—"}
          </Text>
          {t.userName && (
            <Text variant="bodyXs" tone="muted" className="truncate">
              {t.userName}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: "id",
      label: "Transaction ID",
      headerClassName: "[flex:2] min-w-0",
      className: "[flex:2] min-w-0",
      render: (t) => (
        <Text variant="bodySm" tone="body" className="truncate">
          {t.id}
        </Text>
      ),
    },
    {
      key: "points",
      label: "Points",
      headerClassName: "[flex:1] min-w-0",
      className: "[flex:1] min-w-0",
      render: (t) => (
        <Text variant="bodySm" tone="dark">
          {t.points.toLocaleString()}
        </Text>
      ),
    },
    {
      key: "email",
      label: "Email",
      headerClassName: "[flex:2.5] min-w-0",
      className: "[flex:2.5] min-w-0",
      render: (t) => (
        <Text variant="bodySm" tone="body" className="truncate">
          {t.email ?? "—"}
        </Text>
      ),
    },
    {
      key: "phoneNumber",
      label: "Phone",
      headerClassName: "[flex:2] min-w-0",
      className: "[flex:2] min-w-0",
      render: (t) => (
        <Text variant="bodySm" tone="body">
          {t.phoneNumber ?? "—"}
        </Text>
      ),
    },
    {
      key: "expiresAt",
      label: "Points Expiring",
      headerClassName: "[flex:2] min-w-0",
      className: "[flex:2] min-w-0",
      render: (t) => {
        const expired = t.isExpired;
        const near = !expired && isNearExpiry(t.expiresAt);
        return (
          <Text
            variant="bodySm"
            tone="body"
            className={expired || near ? "text-accentError" : undefined}
          >
            {formatDate(t.expiresAt)}
          </Text>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      headerClassName: "[flex:1] min-w-0 text-right",
      className: "[flex:1] min-w-0 flex justify-end",
      render: (t) => (
        <Button
          variant="ghost"
          size="sm"
          className="!px-0 !text-textDark"
          onClick={(e) => { e.stopPropagation(); onView(t); }}
        >
          View
        </Button>
      ),
    },
  ];
}
